import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Funnel } from "lucide-react";

interface PipelineHeaderFiltersPopoverProps {
  showBusinessDropdown: boolean;
  selectedBusiness: string;
  setSelectedBusiness?: (val: string) => void;
  businessOptions: { value: string; label: string }[];

  showHoBDropdown: boolean;
  selectedHoB: string | null;
  setSelectedHoB: (val: string | null) => void;
  hobOptions: { value: string; label: string }[];

  showOSMDropdown: boolean;
  selectedOSM: string | null;
  setSelectedOSM: (val: string | null) => void;
  osmOptions: { value: string; label: string }[];

  selectedTeamId: string | null;
  setSelectedTeamId: (val: string) => void;
  teamOptions: { value: string; label: string }[];

  selectedAgentId: number | null;
  setSelectedAgentId: (val: number) => void;
  agentOptions: { value: number; label: string }[];
}

export const PipelineHeaderFiltersPopover = ({
  showBusinessDropdown,
  selectedBusiness,
  setSelectedBusiness,
  businessOptions,
  showHoBDropdown,
  selectedHoB,
  setSelectedHoB,
  hobOptions,
  showOSMDropdown,
  selectedOSM,
  setSelectedOSM,
  osmOptions,
  selectedTeamId,
  setSelectedTeamId,
  teamOptions,
  selectedAgentId,
  setSelectedAgentId,
  agentOptions,
}: PipelineHeaderFiltersPopoverProps) => {
  const activeFilterCount =
    (showBusinessDropdown && selectedBusiness !== "Whole Business" ? 1 : 0) +
    (showHoBDropdown && selectedHoB !== null ? 1 : 0) +
    (showOSMDropdown && selectedOSM !== null ? 1 : 0) +
    (selectedTeamId !== null && selectedTeamId !== "all" ? 1 : 0) +
    (selectedAgentId !== null && selectedAgentId !== -1 ? 1 : 0);

  const handleClearFilters = () => {
    if (showBusinessDropdown && setSelectedBusiness) {
      setSelectedBusiness("Whole Business");
    }
    if (showHoBDropdown) setSelectedHoB(null);
    if (showOSMDropdown) setSelectedOSM(null);
    setSelectedTeamId("all");
    setSelectedAgentId(-1);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
          <Funnel className="text-muted-foreground size-3.5" />
          Filters
          {activeFilterCount > 0 && <Badge className="ml-1 h-5 min-w-5 px-1.5">{activeFilterCount}</Badge>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex flex-col gap-4 p-3">
          {showBusinessDropdown && (
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">Business</label>
              <Select value={selectedBusiness} onValueChange={(val) => setSelectedBusiness && setSelectedBusiness(val)}>
                <SelectTrigger className="w-full text-sm">
                  <SelectValue placeholder="Business" />
                </SelectTrigger>
                <SelectContent position="popper">
                  {businessOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {showHoBDropdown && (
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">Head of Sales</label>
              <Select value={selectedHoB || "all"} onValueChange={(val) => setSelectedHoB(val === "all" ? null : val)}>
                <SelectTrigger className="w-full text-sm">
                  <SelectValue placeholder="Head of Sales" />
                </SelectTrigger>
                <SelectContent position="popper">
                  <SelectItem value="all">All Heads of Sales</SelectItem>
                  {hobOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {showOSMDropdown && (
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">Sales Manager</label>
              <Select value={selectedOSM || "all"} onValueChange={(val) => setSelectedOSM(val === "all" ? null : val)}>
                <SelectTrigger className="w-full text-sm">
                  <SelectValue placeholder="Sales Manager" />
                </SelectTrigger>
                <SelectContent position="popper">
                  <SelectItem value="all">All Sales Managers</SelectItem>
                  {osmOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">Team</label>
            <Select value={selectedTeamId || "all"} onValueChange={(val) => setSelectedTeamId(val)}>
              <SelectTrigger className="w-full text-sm">
                <SelectValue placeholder="Team" />
              </SelectTrigger>
              <SelectContent position="popper">
                <SelectItem value="all">All Teams</SelectItem>
                {teamOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">Agent</label>
            <Select value={String(selectedAgentId ?? -1)} onValueChange={(val) => setSelectedAgentId(Number(val))}>
              <SelectTrigger className="w-full text-sm">
                <SelectValue placeholder="Agent" />
              </SelectTrigger>
              <SelectContent position="popper">
                <SelectItem value="-1">All Agents</SelectItem>
                {agentOptions.map((opt) => (
                  <SelectItem key={opt.value} value={String(opt.value)}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {activeFilterCount > 0 && (
            <Button variant="ghost" className="w-full" onClick={handleClearFilters}>
              Clear Filters
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
